import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { RefreshCw, Receipt } from "lucide-react";
import { toast } from "sonner";

const BLANK = {
  alignment_number: "", alignment_date: "",
  oneoff_number: "", oneoff_date: "",
  onboarding_number: "", onboarding_date: "",
};

const FIELDS = [
  { key: "alignment", label: "Alignment instalment invoice" },
  { key: "oneoff", label: "One-off invoice" },
  { key: "onboarding", label: "Onboarding invoice" },
];

// Re-sync an accepted proposal to Xero (safely fills any missing alignment instalment
// invoice without duplicating existing ones) and record invoice numbers/dates for
// invoices that were raised manually inside Xero.
export const XeroResyncDialog = ({ proposalId, reference, onDone, trigger }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [vals, setVals] = useState(BLANK);

  const prefill = async () => {
    try {
      const { data } = await api.get(`/proposals/${proposalId}`);
      const m = data?.xero_sync?.manual || {};
      setVals({ ...BLANK, ...m });
    } catch { setVals(BLANK); }
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) prefill();
  };

  const resync = async () => {
    setResyncing(true);
    try {
      const { data } = await api.post(`/xero/sync/${proposalId}`);
      if (data.alignment_error) toast.error(`Alignment invoice failed: ${data.alignment_error}`);
      else if (data.alignment_repeating_invoice_id) toast.success("Alignment instalment invoice created in Xero");
      else toast.success("Re-synced to Xero — nothing new to create");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-sync failed — connect Xero in Admin → Integrations");
    } finally { setResyncing(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/xero/manual-invoice/${proposalId}`, vals);
      toast.success("Invoice details saved");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save invoice details");
    } finally { setBusy(false); }
  };

  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm" variant="outline" data-testid="xero-resync-open">
            <RefreshCw className="w-3.5 h-3.5 mr-1" />Re-sync / invoice
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="xero-resync-dialog">
        <DialogHeader><DialogTitle>Xero — {reference}</DialogTitle></DialogHeader>
        <div className="space-y-5 text-sm">
          <div className="border border-border rounded-md p-3 space-y-2">
            <div className="text-xs font-semibold text-cass-navy">Re-sync to Xero</div>
            <p className="text-[11px] text-cass-slate">
              Creates any missing alignment instalment invoice for this proposal, then pulls the
              latest invoice numbers &amp; dates from Xero back into the report. Existing repeating
              and one-off invoices are left untouched (no duplicates).
            </p>
            <Button size="sm" onClick={resync} disabled={resyncing} className="bg-cass-navy text-white" data-testid="xero-resync-run">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />{resyncing ? "Re-syncing\u2026" : "Re-sync now"}
            </Button>
          </div>

          <div className="border border-border rounded-md p-3 space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-cass-navy">
              <Receipt className="w-3.5 h-3.5" />Enter invoice manually
            </div>
            <p className="text-[11px] text-cass-slate">
              If an invoice was raised directly inside Xero, record its number and date here so it
              shows on the Won proposals report. Leave blank to keep the auto-synced value.
            </p>
            {FIELDS.map((f) => (
              <div key={f.key} className="grid grid-cols-[1fr_130px] gap-2 items-end">
                <div>
                  <Label className="text-[11px] text-cass-slate">{f.label} — number</Label>
                  <Input value={vals[`${f.key}_number`]} onChange={(e) => set(`${f.key}_number`, e.target.value)}
                    placeholder="INV-0000" className="h-8" data-testid={`xero-manual-${f.key}-number`} />
                </div>
                <div>
                  <Label className="text-[11px] text-cass-slate">Date</Label>
                  <Input type="date" value={vals[`${f.key}_date`]} onChange={(e) => set(`${f.key}_date`, e.target.value)}
                    className="h-8" data-testid={`xero-manual-${f.key}-date`} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="xero-resync-close">Close</Button>
          <Button onClick={save} disabled={busy} className="bg-cass-teal text-white" data-testid="xero-manual-save">
            {busy ? "Saving\u2026" : "Save invoice details"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
